import { useFormikContext } from 'formik';
import { Box, Typography } from '@mui/material';
import { theme } from 'bs-unified-ui';
import { useCallback, useState, useEffect } from 'react';
import { Icon as CIcon } from '~/components/common/Icon';

interface ContentImageUploadFieldProps {
  name: string;
  label?: string;
}

const ContentImageUploadField = ({
  name,
  label = 'Upload image',
}: ContentImageUploadFieldProps) => {
  const {
    values,
    setFieldValue,
    setFieldTouched,
    validateField,
    errors,
    touched,
  } = useFormikContext<any>();

  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const fileValue = values[name];

  const handleFileSelect = useCallback(
    (file: File | null) => {

      if (!file) {
        // Keep the existing value if it's a string URL, otherwise set to empty string
        const currentValue = values[name];
        const newValue =
          typeof currentValue === 'string' && currentValue ? currentValue : '';
        setFieldValue(name, newValue, false);
        setImagePreview(
          typeof currentValue === 'string' && currentValue ? currentValue : null,
        );
        setFieldTouched(name, true);
        validateField(name);
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        // Keep the existing value if it's a string URL, otherwise set to empty string
        const currentValue = values[name];
        const newValue =
          typeof currentValue === 'string' && currentValue ? currentValue : '';
        setFieldValue(name, newValue, false);
        setImagePreview(
          typeof currentValue === 'string' && currentValue ? currentValue : null,
        );
        setFieldTouched(name, true);
        return;
      }

      // Set the file value and let validation schema handle file size check
      setFieldValue(name, file, false);
      setFieldTouched(name, true, false);

      // Generate preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Trigger validation after state update
      setTimeout(() => {
        validateField(name);
      }, 0);
    },
    [name, setFieldValue, setFieldTouched, validateField, values],
  );

  useEffect(() => {
    // Handle preview for File object or URL string
    if (!fileValue) {
      setImagePreview(null);
    } else if (fileValue instanceof File) {
      // File object - generate preview from FileReader
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setImagePreview(preview);
      };
      reader.readAsDataURL(fileValue);
    } else if (typeof fileValue === 'string') {
      // URL string from backend
      setImagePreview(fileValue);
    }
  }, [fileValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Only handle if a file was actually selected (not cancelled)
    if (file) {
      handleFileSelect(file);
    }
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const error = errors[name];
  const showError = error && touched[name];

  return (
    <Box>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='fs14' color={theme.foreground.black}>
          {label}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        {imagePreview && (
          <Box
            sx={{
              backgroundColor: 'white',
              height: '135px',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <img
              src={imagePreview}
              alt='preview'
              style={{
                width: 'auto',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <input
            type='file'
            accept='image/jpeg,image/png'
            onChange={handleInputChange}
            style={{
              display: 'none',
            }}
            id={`${name}-file-input`}
          />
          <label
            htmlFor={`${name}-file-input`}
            style={{
              cursor: 'pointer',
              display: 'block',
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: theme.foreground.black,
                fontWeight: 600,
                px: 1,
                py: 0.5,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <CIcon name='upload-cloud-gray' size={16} useBackgroundImg />
              Upload image{' '}
            </Typography>
          </label>
        </Box>
      </Box>
      {showError && (
        <Typography
          sx={{
            fontSize: 12,
            color: '#F24822',
            mt: 0.5,
          }}
        >
          {error as string}
        </Typography>
      )}
    </Box>
  );
};

export default ContentImageUploadField;
